import type { FormConfig } from '../../config/types';
import type { FetchFormResponse } from '../../service/types';
import { Status } from '../../types';
import {
  getEnabledSubsections,
  getOverviewStatus,
  isWorkflowOnlyPageName,
  normalizeQuarterCode,
} from '../../utils/config-utils';

export const prerequisites = [
  ['cover-sheet', '1.1 Your Details'],
  ['business-objective', '2.1 Business Objectives'],
  ['finance', '3.1 Finance'],
  ['corportate', '3.2 Corporate'],
  ['leadership', '3.3 Leadership'],
  ['diversity-inclusion', '3.4 Diversity & Inclusion'],
] as const;

const latestActiveQuarter = (config: FormConfig, data?: FetchFormResponse) =>
  data?.scsProgress?.currentQuarter
    ? config.sections.find(
        (section) => section.quarterReview?.quarter === data.scsProgress!.currentQuarter,
      )
    : undefined;

const hasSubmittedQuarter = (data: FetchFormResponse | undefined, quarter: string) =>
  data?.approvedQuarters?.includes(quarter) ||
  normalizeQuarterCode(data?.approvedQuarter || data?.approvalStatus) === quarter ||
  normalizeQuarterCode(data?.pendingApprovalQuarter) === quarter;

export function getScsSubmissionSections(config: FormConfig, data?: FetchFormResponse) {
  const latest = latestActiveQuarter(config, data);
  if (!latest) return [];
  if (hasSubmittedQuarter(data, latest.quarterReview!.quarter)) return [latest];
  return config.sections.flatMap((section) => {
    if (section === latest) return [section];
    const subsections = section.subsections.filter((subsection) =>
      prerequisites.some(([slug]) => slug === subsection.slug),
    );
    return subsections.length ? [{ ...section, subsections }] : [];
  });
}

export function getQuarterCompletion(
  config: FormConfig,
  data: FetchFormResponse,
  savedSubsection: string,
) {
  const section = latestActiveQuarter(config, data);
  if (!section) return null;
  const quarter = section.quarterReview!.quarter;
  if (hasSubmittedQuarter(data, quarter)) return null;
  if (
    !section.subsections.some((entry) => entry.slug === savedSubsection) &&
    !prerequisites.some(([slug]) => slug === savedSubsection)
  )
    return null;
  const enabled = getEnabledSubsections(config, data);
  const statuses = getOverviewStatus(config.sections, enabled, data);
  const staffSubsections = section.subsections.filter((entry) =>
    entry.pages.some((page) => !isWorkflowOnlyPageName(page.name)),
  );
  if (
    !staffSubsections.length ||
    staffSubsections.some((entry) => statuses[entry.slug] !== Status.Completed)
  )
    return null;
  return {
    quarter: section.quarterReview!.quarter,
    missing: prerequisites
      .filter(([slug]) => statuses[slug] !== Status.Completed)
      .map(([, label]) => label),
  };
}
